// Libraries
import { NextRouter, useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
// Types
import { Filter, FilterContext, UrlFilterOptions } from './useUrlFilters.type';


/**
 * Parses the given filters and options into a URL query string.
 *
 * @param {Filter[]} filters - The array of filters.
 * @param {UrlFilterOptions} options - The options for parsing the filters.
 * @returns {string} The URL query string (starting with '?') if there are filters, otherwise an empty string.
 */
export const parseToUrlQueryString = (filters: Filter[], options: UrlFilterOptions): string => {
  const { encode, filterSeparator, valueSeparator, optionSeparator } = options;

  /**
   * Parses the given text based on the encoding option.
   *
   * @param {string} text - The text to be parsed.
   * @returns {string} URI encoded text if encode is true, otherwise return the same text.
   */
  const parse = (text: string): string => (encode ? encodeURIComponent(text) : text);

  /**
   * Receives a string or and array of strings and returns an array of parsed values,
   *
   * @param {string | string[]} value The value (or values) that come from the filter.
   * @returns {string[]} A parsed value array.
   */
  const parseValue = (value: string | string[]): string[] => (Array.isArray(value) ? value.map(parse) : [value]);

  if (filters.length == 0) {
    return '';
  }

  return (
    '?' +
    filters
      .map((filter) => `${parse(filter.id)}${valueSeparator}${parseValue(filter.value).join(optionSeparator)}`)
      .join(filterSeparator)
  );
};

/**
 * Creates a filter function based on the provided options.
 *
 * @param {UrlFilterOptions} options - The options for creating the filter function.
 * @returns {Function} The filter function.
 */
export const paramToFilter =
  (options: UrlFilterOptions) =>
  (param: string): Filter => {
    const { encode, optionSeparator, valueSeparator } = options;
    const [name, valueString] = param.split(valueSeparator);

    /**
     * Parses the given text based on the encoding option.
     *
     * @param {string} text - The text to be parsed.
     * @returns {string} URI decoded text if encode is true, otherwise the same text.
     */
    const parse = (text: string): string => (encode ? decodeURIComponent(text) : text);

    return { id: parse(name), value: valueString.split(optionSeparator).map(parse) };
  };

export const defaultOptions: UrlFilterOptions = {
  encode: false,
  filterSeparator: '&',
  valueSeparator: '=',
  optionSeparator: ',',
};

/**
 * Parses the filters from the router's URL query string.
 *
 * @param {NextRouter} router - The Next.js router object.
 * @param {UrlFilterOptions} options - The options for parsing the filters.
 * @returns {Filter[]} The array of parsed filters.
 */
export const parseFromUrlToFilters = (router: Pick<NextRouter, 'asPath'>, options: UrlFilterOptions): Filter[] => {
  const { filterSeparator } = options;

  if (router.asPath.indexOf('?') == -1) {
    return [];
  }
  const queryString = router.asPath.replace(/.*\?(.*)$/, '$1');
  const params = queryString.split(filterSeparator);
  const filters = params.map(paramToFilter(options));

  return filters;
};

/**
 * Hook for managing table filters based on the URL query string.
 *
 * @template T - The type of data in the table.
 * @param {FilterContext<T>} context - The table instance.
 * @param {Partial<UrlFilterOptions>} options - The options for parsing the filters.
 */
export const useUrlFilters = <T>(context: FilterContext<T>, options: Partial<UrlFilterOptions> = {}) => {
  const router = useRouter();
  const state = context.getState();
  const readyRef = useRef(false);

  const parsingOptions: UrlFilterOptions = {
    ...defaultOptions,
    ...options,
  };
  
  useEffect(() => {
    const filters = parseFromUrlToFilters(router, parsingOptions);
    context.setColumnFilters(filters);

    // ?NOTE: This prevents deleting the applied filters in the url when loading a link, by avoiding to push in the first render
    const timerId = setTimeout(() => {
      readyRef.current = true;
    }, 0);

    return () => {
      clearTimeout(timerId);
    };

    // ?NOTE: this should execute in the first render only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (readyRef.current) {
      const stringifiedQuery = parseToUrlQueryString(state.columnFilters as Filter[], parsingOptions);
      const nextRoute = router.route + stringifiedQuery;

      if (nextRoute !== router.asPath) {
        router.push(nextRoute);
      }
    }
    // ?NOTE: router excluded due to it caused a re-render loop, but no router prop or method need to be included for the correct work.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.columnFilters]);
};
